import React, { useState, useEffect, useCallback } from 'react'
import {
  Users,
  RefreshCw,
  AlertCircle,
  Search,
  Shield,
  User,
  CreditCard,
  Zap,
  CheckCircle,
  XCircle,
  ChevronDown,
  ChevronRight,
  Settings,
  Mail,
  Calendar,
  Activity
} from 'lucide-react'
import clsx from 'clsx'
import * as adminApi from '../../services/adminApi'
import ConfirmDialog from '../../components/admin/ConfirmDialog'
import Toast, { ToastContainer } from '../../components/admin/Toast'

// Plan configuration
const planConfig = {
  free: { label: 'Free', color: 'bg-gray-500', textColor: 'text-gray-700' },
  starter: { label: 'Starter', color: 'bg-blue-500', textColor: 'text-blue-700' },
  professional: { label: 'Professional', color: 'bg-indigo-500', textColor: 'text-indigo-700' },
  enterprise: { label: 'Enterprise', color: 'bg-purple-500', textColor: 'text-purple-700' },
}

// Status badge component
const StatusBadge = ({ status }) => {
  const isActive = status === 'active'
  return (
    <span className={clsx(
      'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium',
      isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
    )}>
      {isActive ? <CheckCircle className="w-3 h-3 mr-1" /> : <XCircle className="w-3 h-3 mr-1" />}
      {status}
    </span>
  )
}

const UsersPage = () => {
  // State
  const [users, setUsers] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [searchQuery, setSearchQuery] = useState('')

  // Expanded users for detail view
  const [expandedUser, setExpandedUser] = useState(null)
  const [userDetails, setUserDetails] = useState({})
  const [loadingDetails, setLoadingDetails] = useState({})

  // Sync states
  const [isSyncing, setIsSyncing] = useState({})
  const [isBulkSyncing, setIsBulkSyncing] = useState(false)

  // Toast state
  const [toasts, setToasts] = useState([])

  // Add toast helper
  const addToast = useCallback((message, type = 'info') => {
    const id = Date.now()
    setToasts(prev => [...prev, { id, message, type }])
  }, [])

  const removeToast = useCallback((id) => {
    setToasts(prev => prev.filter(t => t.id !== id))
  }, [])

  // Fetch users list
  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const data = await adminApi.getUsers()
      setUsers(data)
    } catch (err) {
      console.error('Failed to fetch users:', err)
      setError(err.response?.data?.detail || 'Failed to load users')
      addToast('Failed to load users', 'error')
    } finally {
      setLoading(false)
    }
  }, [addToast])

  useEffect(() => {
    fetchUsers()
  }, [fetchUsers])

  // Fetch user subscription details
  const fetchUserDetails = useCallback(async (userId) => {
    if (userDetails[userId]) return // Already loaded

    try {
      setLoadingDetails(prev => ({ ...prev, [userId]: true }))
      const data = await adminApi.getUserSubscriptionStatus(userId)
      setUserDetails(prev => ({ ...prev, [userId]: data }))
    } catch (err) {
      console.error('Failed to fetch user details:', err)
      addToast('Failed to load user details', 'error')
    } finally {
      setLoadingDetails(prev => ({ ...prev, [userId]: false }))
    }
  }, [userDetails, addToast])

  // Toggle user expansion
  const toggleUser = (userId) => {
    if (expandedUser === userId) {
      setExpandedUser(null)
    } else {
      setExpandedUser(userId)
      fetchUserDetails(userId)
    }
  }

  // Sync individual user subscription
  const handleSyncUser = async (userId, e) => {
    e?.stopPropagation()
    try {
      setIsSyncing(prev => ({ ...prev, [userId]: true }))
      const result = await adminApi.syncUserSubscription(userId)
      if (result.changes_made) {
        addToast('User subscription synced successfully', 'success')
        // Refresh user details
        setUserDetails(prev => ({ ...prev, [userId]: null }))
        fetchUserDetails(userId)
      } else {
        addToast('No changes needed', 'info')
      }
    } catch (err) {
      console.error('Failed to sync user:', err)
      addToast(err.response?.data?.detail || 'Failed to sync user subscription', 'error')
    } finally {
      setIsSyncing(prev => ({ ...prev, [userId]: false }))
    }
  }

  // Bulk sync all subscriptions
  const handleBulkSync = async () => {
    try {
      setIsBulkSyncing(true)
      const result = await adminApi.fixSubscriptions()
      addToast(`Synced ${result.stats?.synced || 0} subscriptions`, 'success')
      // Refresh users and clear cached details
      setUserDetails({})
      fetchUsers()
    } catch (err) {
      console.error('Failed to bulk sync:', err)
      addToast(err.response?.data?.detail || 'Failed to sync subscriptions', 'error')
    } finally {
      setIsBulkSyncing(false)
    }
  }

  // Filter users by search
  const filteredUsers = users.filter(user => {
    if (!searchQuery) return true
    const query = searchQuery.toLowerCase()
    return (
      user.email?.toLowerCase().includes(query) ||
      user.role?.toLowerCase().includes(query) ||
      user.subscription?.plan_name?.toLowerCase().includes(query)
    )
  })

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A'
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  }

  // Format number
  const formatNumber = (num) => {
    if (!num) return '0'
    return num.toLocaleString()
  }

  // Render loading state
  if (loading && users.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-indigo-600" />
        <span className="ml-3 text-gray-600">Loading users...</span>
      </div>
    )
  }

  return (
    <div className="space-y-6" data-testid="users-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 flex items-center gap-2">
            <Users className="w-6 h-6" />
            User Management
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Manage user subscriptions and features
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => fetchUsers()}
            disabled={loading}
            className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
          >
            <RefreshCw className={clsx('w-4 h-4 mr-2', loading && 'animate-spin')} />
            Refresh
          </button>
          <button
            onClick={handleBulkSync}
            disabled={isBulkSyncing}
            data-testid="sync-all-subscriptions-button"
            className="inline-flex items-center px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-md hover:bg-indigo-700 disabled:opacity-50"
          >
            {isBulkSyncing ? (
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Zap className="w-4 h-4 mr-2" />
            )}
            Sync All Subscriptions
          </button>
        </div>
      </div>

      {/* Error display */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center text-red-800">
            <AlertCircle className="w-5 h-5 mr-2" />
            {error}
          </div>
        </div>
      )}

      {/* Search and Stats */}
      <div className="flex items-center justify-between">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search by email, role, or plan..."
            data-testid="users-search-input"
            className="pl-10 pr-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm focus:ring-indigo-500 focus:border-indigo-500 w-80"
          />
        </div>
        <span className="text-sm text-gray-500" data-testid="users-count">
          {filteredUsers.length} of {users.length} users
        </span>
      </div>

      {/* Users List */}
      <div className="bg-white rounded-lg shadow border overflow-hidden" data-testid="users-table-container">
        <table className="min-w-full divide-y divide-gray-200" data-testid="users-table">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-8"></th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Role</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Plan</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Limit</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredUsers.map(user => {
              const isExpanded = expandedUser === user.id
              const details = userDetails[user.id]
              const isLoadingDetails = loadingDetails[user.id]
              const plan = planConfig[user.subscription?.plan_name] || planConfig.free

              return (
                <React.Fragment key={user.id}>
                  {/* User Row */}
                  <tr
                    className={clsx('hover:bg-gray-50 cursor-pointer', isExpanded && 'bg-indigo-50')}
                    onClick={() => toggleUser(user.id)}
                    data-testid="user-row"
                    data-user-id={user.id}
                    data-user-email={user.email}
                  >
                    <td className="px-4 py-3">
                      {isExpanded ? (
                        <ChevronDown className="w-4 h-4 text-gray-400" />
                      ) : (
                        <ChevronRight className="w-4 h-4 text-gray-400" />
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center">
                        <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center mr-3">
                          {user.role === 'admin' ? (
                            <Shield className="w-4 h-4 text-amber-600" />
                          ) : (
                            <User className="w-4 h-4 text-gray-500" />
                          )}
                        </div>
                        <div>
                          <div className="text-sm font-medium text-gray-900">{user.email}</div>
                          <div className="text-xs text-gray-500">ID: {user.id}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={clsx(
                        'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium',
                        user.role === 'admin' ? 'bg-amber-100 text-amber-800' : 'bg-gray-100 text-gray-800'
                      )}>
                        {user.role}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={clsx(
                        'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium',
                        plan.color.replace('bg-', 'bg-opacity-20 '),
                        plan.textColor
                      )}>
                        <CreditCard className="w-3 h-3 mr-1" />
                        {plan.label}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <StatusBadge status={user.subscription?.plan_status || 'inactive'} />
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">
                      {formatNumber(user.subscription?.monthly_character_limit)}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">
                      {formatDate(user.created_at)}
                    </td>
                    <td className="px-4 py-3">
                      <button
                        onClick={(e) => handleSyncUser(user.id, e)}
                        disabled={isSyncing[user.id]}
                        className="inline-flex items-center px-2 py-1 text-xs font-medium text-indigo-600 hover:text-indigo-800 disabled:opacity-50"
                      >
                        {isSyncing[user.id] ? (
                          <RefreshCw className="w-3 h-3 mr-1 animate-spin" />
                        ) : (
                          <Settings className="w-3 h-3 mr-1" />
                        )}
                        Sync
                      </button>
                    </td>
                  </tr>

                  {/* Expanded Details Row */}
                  {isExpanded && (
                    <tr className="bg-gray-50">
                      <td colSpan={8} className="px-8 py-4">
                        {isLoadingDetails ? (
                          <div className="flex items-center justify-center py-4">
                            <RefreshCw className="w-5 h-5 animate-spin text-indigo-600" />
                            <span className="ml-2 text-sm text-gray-500">Loading details...</span>
                          </div>
                        ) : details ? (
                          <div className="grid grid-cols-3 gap-6">
                            {/* Subscription Info */}
                            <div className="bg-white rounded-lg p-4 shadow-sm">
                              <h4 className="font-medium text-gray-900 mb-3 flex items-center">
                                <CreditCard className="w-4 h-4 mr-2" />
                                Subscription Details
                              </h4>
                              {details.subscription ? (
                                <dl className="space-y-2 text-sm">
                                  <div className="flex justify-between">
                                    <dt className="text-gray-500">Plan:</dt>
                                    <dd className="font-medium">{details.subscription.plan_name}</dd>
                                  </div>
                                  <div className="flex justify-between">
                                    <dt className="text-gray-500">Status:</dt>
                                    <dd><StatusBadge status={details.subscription.plan_status} /></dd>
                                  </div>
                                  <div className="flex justify-between">
                                    <dt className="text-gray-500">Character Limit:</dt>
                                    <dd className="font-medium">{formatNumber(details.subscription.monthly_character_limit)}</dd>
                                  </div>
                                </dl>
                              ) : (
                                <p className="text-sm text-gray-500 italic">No subscription</p>
                              )}
                            </div>

                            {/* Features */}
                            <div className="bg-white rounded-lg p-4 shadow-sm">
                              <h4 className="font-medium text-gray-900 mb-3 flex items-center">
                                <Activity className="w-4 h-4 mr-2" />
                                Features
                              </h4>
                              {details.subscription ? (
                                <ul className="space-y-2 text-sm">
                                  <li className="flex items-center">
                                    {details.subscription.has_phrase_integration ? (
                                      <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
                                    ) : (
                                      <XCircle className="w-4 h-4 text-gray-300 mr-2" />
                                    )}
                                    Phrase Integration
                                  </li>
                                  <li className="flex items-center">
                                    {details.subscription.has_quality_review ? (
                                      <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
                                    ) : (
                                      <XCircle className="w-4 h-4 text-gray-300 mr-2" />
                                    )}
                                    Quality Review
                                  </li>
                                  <li className="flex items-center">
                                    {details.subscription.has_priority_support ? (
                                      <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
                                    ) : (
                                      <XCircle className="w-4 h-4 text-gray-300 mr-2" />
                                    )}
                                    Priority Support
                                  </li>
                                </ul>
                              ) : (
                                <p className="text-sm text-gray-500 italic">N/A</p>
                              )}
                            </div>

                            {/* TMS Access */}
                            <div className="bg-white rounded-lg p-4 shadow-sm">
                              <h4 className="font-medium text-gray-900 mb-3 flex items-center">
                                <Shield className="w-4 h-4 mr-2" />
                                Access Status
                              </h4>
                              <dl className="space-y-2 text-sm">
                                <div className="flex justify-between">
                                  <dt className="text-gray-500">TMS Access:</dt>
                                  <dd>
                                    {details.tms_access_check ? (
                                      <span className="text-green-600 font-medium">Enabled</span>
                                    ) : (
                                      <span className="text-gray-400">Disabled</span>
                                    )}
                                  </dd>
                                </div>
                                <div className="flex justify-between">
                                  <dt className="text-gray-500">User ID:</dt>
                                  <dd className="font-mono text-xs">{details.user_id}</dd>
                                </div>
                              </dl>
                            </div>
                          </div>
                        ) : (
                          <p className="text-sm text-gray-500 text-center py-4">Unable to load details</p>
                        )}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              )
            })}
          </tbody>
        </table>

        {filteredUsers.length === 0 && (
          <div className="text-center py-12">
            <Users className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">No users found</p>
          </div>
        )}
      </div>

      {/* Toast notifications */}
      <ToastContainer>
        {toasts.map(toast => (
          <Toast
            key={toast.id}
            message={toast.message}
            type={toast.type}
            onClose={() => removeToast(toast.id)}
          />
        ))}
      </ToastContainer>
    </div>
  )
}

export default UsersPage
